/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, OnInit, Optional } from '@angular/core';
import { LoginDetails } from './login-details.model';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { LoginService } from './login.service';
import { TranslateService } from '@ngx-translate/core';
import { chain, split } from 'lodash';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { UserAccountService, UserContext } from '@irian-ro/util-user';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'login-component',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginDetails: LoginDetails | undefined = undefined;
  userUnactivated = false;
  statusMessages: Array<ToastMessageOptions> | undefined = undefined;

  private returnUrlBase = '';
  private returnUrlQueryParams = null;

  constructor(
    @Optional() private readonly loginService: LoginService,
    @Optional() private readonly userContext: UserContext,
    private readonly userAccountService: UserAccountService,
    private readonly translateService: TranslateService,
    private readonly messageService: MessageService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute
  ) {
    if (!userContext) {
      throw new Error('Provide UserContext in app.module');
    }

    if (!loginService) {
      throw new Error('Provide loginService in app.module');
    }
  }

  ngOnInit() {
    // TODO: implement logout onError
    this.loginService.logout(false).subscribe();
    this.loginDetails = new LoginDetails();
    this.activatedRoute.queryParamMap.subscribe((paramMap: ParamMap) => {
      const returnUrl = paramMap.get('returnUrl') || '';
      const splitUrl = returnUrl.split('?');
      this.returnUrlBase = splitUrl[0];
      this.returnUrlQueryParams = this.extractQueryParamsFromSplitUrl(splitUrl);
      const profileInfoChanged: string = paramMap.get('profileInfoChanged');
      const statusMessages: string = paramMap.get('statusMessages');
      if (profileInfoChanged && JSON.parse(profileInfoChanged)) {
        this.translateService.stream('auth.profileInfoChanged').subscribe((resMessage: string) => {
          this.statusMessages = [];
          this.statusMessages.push({
            severity: 'success',
            summary: resMessage,
          });
        });
      }
      if (statusMessages) {
        const statusMessagesParsed = JSON.parse(statusMessages);
        if (statusMessagesParsed) {
          this.statusMessages = statusMessagesParsed;
        }
      }
    });
  }

  login() {
    this.loginService.doLogin(this.loginDetails).subscribe(
      () => {
        this.userUnactivated = false;
        this.messageService.clear();
        this.userContext.getLoggedInUserFromServer().subscribe();
        this.router.navigate([`/${this.returnUrlBase}`], {
          queryParams: this.returnUrlQueryParams,
        });
      },
      (res: HttpErrorResponse) => {

        this.router.navigate([`/${this.returnUrlBase}`], {
          queryParams: this.returnUrlQueryParams,
        });

        if (res) {
          if (res.error) {
            this.handleLoginError(res);
          }
        }
      }
    );
  }

  resendEmailConfirmation() {
    if (this.userUnactivated) {
      this.userAccountService.resendEmailConfirmation(this.loginDetails?.username).subscribe(
        () => {
          this.translateService.stream('auth.resend-email-confirmation.success').subscribe((resMessage: string) => {
            this.statusMessages = [];
            this.statusMessages.push({
              severity: 'success',
              summary: resMessage,
            });
            this.userUnactivated = false;
          });
        },
        () => {
          this.translateService.stream('auth.resend-email-confirmation.error').subscribe((resMessage: string) => {
            this.statusMessages = [];
            this.statusMessages.push({
              severity: 'error',
              summary: resMessage,
            });
          });
        }
      );
    }
  }

  public goToPasswordRecoveryRequest() {
    this.router.navigate(['/auth/password-recovery-request']);
  }

  private handleLoginError(res: HttpErrorResponse) {
    this.userUnactivated = false;
    let errorTranslationKey = '';
    if (res.status === 504) {
      errorTranslationKey += res.status;
    } else {
      errorTranslationKey += res.error['error_description'];
      if (res.error['error_description'] === 'disabled-user') {
        this.userUnactivated = true;
      }
    }

    this.translateService.stream('auth.error.' + errorTranslationKey).subscribe((resMessage: string) => {
      this.statusMessages = [];
      this.statusMessages.push({ severity: 'error', summary: resMessage });
    });
  }

  private extractQueryParamsFromSplitUrl(splitUrl: Array<string>) {
    let queryParamsObject = null;
    if (splitUrl.length > 1) {
      queryParamsObject = chain(splitUrl[1])
        .split('&')
        .map((pair) => {
          return split(pair, '=');
        })
        .fromPairs()
        .value();
    }
    return queryParamsObject ? queryParamsObject : null;
  }
}
